import { useEffect, useState } from "react";
import { Loader2, ReceiptText, Plus, Trash2, Eye, FileDown, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatApiError } from "../api";

const STATUS_PEMBAYARAN = ["Belum Dibayar", "Sebagian", "Lunas"];
const statusBadge = {
  "Belum Dibayar": "bg-red-100 text-red-800 border border-red-200",
  Sebagian: "bg-blue-100 text-blue-800 border border-blue-200",
  Lunas: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};
const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const fmtKG = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} KG`;
const fmtDate = (s) => {
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d) ? s : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};
const today = () => new Date().toISOString().slice(0, 10);

const KOMODITAS_OPSI = ["Semua", "CPO", "Palm Kernel", "Cangkang"];

const DraftDialog = ({ open, onClose, klienList, onSaved }) => {
  const [form, setForm] = useState({ Klien_Dituju: "", Tanggal_Terbit: today(), date_from: "", date_to: "", Harga_Angkut_Per_KG: "", Jenis_Komoditas: "Semua" });
  const [saving, setSaving] = useState(false);
  const [tarif, setTarif] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ Klien_Dituju: "", Tanggal_Terbit: today(), date_from: "", date_to: "", Harga_Angkut_Per_KG: "", Jenis_Komoditas: "Semua" });
      api.get("/invoice/tarif-default").then(({ data }) => setTarif(data)).catch(() => setTarif(null));
    }
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pilihKomoditas = (k) => setForm((f) => ({
    ...f, Jenis_Komoditas: k,
    Harga_Angkut_Per_KG: k !== "Semua" && tarif?.[k] ? String(tarif[k]) : f.Harga_Angkut_Per_KG,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Klien_Dituju || !form.date_from || !form.date_to || !form.Harga_Angkut_Per_KG) {
      toast.error("Semua kolom wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/invoice/draft", { ...form, Harga_Angkut_Per_KG: parseFloat(form.Harga_Angkut_Per_KG) });
      toast.success(`Draft ${data.No_Invoice} dibuat: ${data.trips.length} trip, ${fmtKG(data.Total_Tonase_Ditagihkan)}, total ${fmtRp(data.Total_Tagihan)}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="invoice-draft-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Buat Draft Invoice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Klien Dituju (tujuan bongkar)</Label>
            <Select value={form.Klien_Dituju} onValueChange={set("Klien_Dituju")}>
              <SelectTrigger data-testid="invoice-klien-select"><SelectValue placeholder="Pilih klien / tujuan bongkar" /></SelectTrigger>
              <SelectContent>
                {klienList.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-400">Daftar diambil dari Tujuan Bongkar trip berstatus Selesai</p>
          </div>
          <div className="space-y-1.5">
            <Label>Jenis Komoditas</Label>
            <Select value={form.Jenis_Komoditas} onValueChange={pilihKomoditas}>
              <SelectTrigger data-testid="invoice-komoditas-select"><SelectValue placeholder="Pilih komoditas" /></SelectTrigger>
              <SelectContent>
                {KOMODITAS_OPSI.map((k) => <SelectItem key={k} value={k}>{k === "Semua" ? "Semua Komoditas" : k}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-400">Hanya trip komoditas terpilih yang ditarik ke invoice ini — harga angkut/KG terisi otomatis dari tarif Pengaturan (bisa diubah)</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Periode Dari (tgl muat)</Label>
              <Input data-testid="invoice-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Sampai</Label>
              <Input data-testid="invoice-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tanggal Terbit</Label>
              <Input data-testid="invoice-tanggal-terbit" type="date" value={form.Tanggal_Terbit} onChange={(e) => set("Tanggal_Terbit")(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Harga Angkut / KG (Rp)</Label>
              <Input data-testid="invoice-harga-input" type="number" min="1" step="0.01" placeholder="cth: 250"
                value={form.Harga_Angkut_Per_KG} onChange={(e) => set("Harga_Angkut_Per_KG")(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
            Sistem otomatis menarik semua trip <b>Selesai</b> milik klien pada rentang tanggal muat tersebut yang <b>belum pernah ditagihkan</b>, lalu menjumlahkan Tonase Bongkar sebagai dasar tagihan.
          </p>
          <DialogFooter>
            <Button data-testid="invoice-draft-cancel" type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button data-testid="invoice-draft-submit" type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Buat Draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const bulanIni = () => new Date().toISOString().slice(0, 7);

const RekapTagihanBulanan = ({ refreshKey }) => {
  const [bulan, setBulan] = useState(bulanIni());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!bulan) return;
    api.get(`/invoice/rekap-bulanan?bulan=${bulan}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [bulan, refreshKey]);

  const exportExcel = async () => {
    try {
      const res = await api.get(`/invoice/rekap-bulanan/export?bulan=${bulan}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rekap_Tagihan_${bulan}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel rekap tagihan diunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    }
  };

  return (
    <div data-testid="rekap-tagihan-card" className="bg-white border border-zinc-200 rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 pt-4">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">Rekap Tagihan Bulanan</h2>
          <p className="text-xs text-zinc-500">Total tagihan per klien berdasarkan tanggal terbit invoice — pantau piutang yang belum lunas</p>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="rekap-bulan-input" type="month" value={bulan} onChange={(e) => setBulan(e.target.value)} className="w-44" />
          <Button data-testid="rekap-export-btn" type="button" variant="outline" size="sm"
            onClick={exportExcel} disabled={!data || data.rows.length === 0}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 transition-colors duration-200">
            <FileDown className="w-4 h-4 mr-1.5" /> Excel
          </Button>
        </div>
      </div>
      <div className="px-5 py-4">
        {!data || data.rows.length === 0 ? (
          <p data-testid="rekap-tagihan-empty" className="text-sm text-zinc-400 py-3 text-center">Tidak ada invoice terbit pada bulan ini</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rekap-tagihan-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Klien", "Invoice", "Total Tagihan", "Sudah Lunas", "Piutang"].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.klien} data-testid={`rekap-row-${r.klien}`} className="border-b border-zinc-100">
                    <td className="px-4 py-2.5 whitespace-nowrap font-medium text-zinc-900">{r.klien}</td>
                    <td className="px-4 py-2.5 tabular-nums">{r.invoice}</td>
                    <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtRp(r.total)}</td>
                    <td className="px-4 py-2.5 tabular-nums whitespace-nowrap text-emerald-700">{fmtRp(r.lunas)}</td>
                    <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap font-semibold ${r.piutang > 0 ? "text-red-700" : "text-zinc-400"}`}>{fmtRp(r.piutang)}</td>
                  </tr>
                ))}
                <tr data-testid="rekap-total-row" className="bg-zinc-50 font-semibold">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="px-4 py-2.5 tabular-nums">{data.totals.invoice}</td>
                  <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtRp(data.totals.total)}</td>
                  <td className="px-4 py-2.5 tabular-nums whitespace-nowrap text-emerald-700">{fmtRp(data.totals.lunas)}</td>
                  <td className="px-4 py-2.5 tabular-nums whitespace-nowrap text-red-700">{fmtRp(data.totals.piutang)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const OADialog = ({ target, onClose, onDone }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      const oa = target.oa || {};
      setForm({
        No_Invoice_Kustom: oa.No_Invoice_Kustom || "",
        No_Kontrak: oa.No_Kontrak || "", No_DO: oa.No_DO || "",
        Keterangan_Lokasi: oa.Keterangan_Lokasi || "",
        Potongan_Susut: String(oa.Potongan_Susut || 0), Potongan_Mutu: String(oa.Potongan_Mutu || 0),
        Nama_Penandatangan: oa.Nama_Penandatangan || "",
      });
    }
  }, [target]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/invoice/${target.id}/oa`, {
        ...form,
        Potongan_Susut: parseFloat(form.Potongan_Susut) || 0,
        Potongan_Mutu: parseFloat(form.Potongan_Mutu) || 0,
      });
      await onDone(target);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="invoice-oa-dialog" className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Invoice Format Resmi — {target?.No_Invoice}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>No. Invoice Kustom (opsional)</Label>
            <Input data-testid="oa-no-invoice-input" value={form.No_Invoice_Kustom || ""} onChange={set("No_Invoice_Kustom")}
              placeholder={`cth: 006/MBA/07/26 — kosongkan untuk memakai ${target?.No_Invoice || "nomor sistem"}`} />
          </div>
          <div className="space-y-1.5">
            <Label>No. Kontrak Angkut (opsional)</Label>
            <Input data-testid="oa-kontrak-input" value={form.No_Kontrak || ""} onChange={set("No_Kontrak")}
              placeholder="cth: 041/ANJAS/INDUK/1/2026" />
          </div>
          <div className="space-y-1.5">
            <Label>No. DO (opsional)</Label>
            <Input data-testid="oa-do-input" value={form.No_DO || ""} onChange={set("No_DO")}
              placeholder="cth: 5021/IT ANJAS/IV/26" />
          </div>
          <div className="space-y-1.5">
            <Label>Keterangan Lokasi (opsional)</Label>
            <Input data-testid="oa-lokasi-input" value={form.Keterangan_Lokasi || ""} onChange={set("Keterangan_Lokasi")}
              placeholder="cth: Tangki Timbun, Pargarutan - Angkola Timur" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Potongan Susut (Rp)</Label>
              <Input data-testid="oa-susut-input" type="number" min="0" step="1" value={form.Potongan_Susut || "0"} onChange={set("Potongan_Susut")} />
            </div>
            <div className="space-y-1.5">
              <Label>Potongan Mutu (Rp)</Label>
              <Input data-testid="oa-mutu-input" type="number" min="0" step="1" value={form.Potongan_Mutu || "0"} onChange={set("Potongan_Mutu")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nama Penandatangan</Label>
            <Input data-testid="oa-ttd-input" value={form.Nama_Penandatangan || ""} onChange={set("Nama_Penandatangan")}
              placeholder="cth: WENDY WIHARTA" />
          </div>
          <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
            PDF mengikuti format resmi: tabel Kirim/Terima/Selisih per komoditas, Dasar Pengenaan Pajak, <b>PPh Final 0,5%</b>, terbilang otomatis, dan tanda tangan. Ongkos kirim dihitung dari <b>tonase Terima × ongkos/kg</b>.
          </p>
          <DialogFooter>
            <Button data-testid="oa-cancel" type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button data-testid="oa-submit" type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Simpan & Unduh PDF
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};


export default function InvoicePage() {
  const [invoices, setInvoices] = useState(null);
  const [klienList, setKlienList] = useState([]);
  const [draftOpen, setDraftOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [oaTarget, setOaTarget] = useState(null);
  const [filterKomoditas, setFilterKomoditas] = useState("Semua");

  const tampil = (invoices || []).filter(
    (inv) => filterKomoditas === "Semua" || (inv.Jenis_Komoditas || "Semua") === filterKomoditas
  );

  const downloadPdf = async (inv, fmt = "standar") => {
    try {
      const res = await api.get(`/invoice/${inv.id}/pdf?fmt=${fmt}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : (fmt === "oa" ? `INVOICE_${inv.No_Invoice}_OA.pdf` : `${inv.No_Invoice}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF invoice diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh PDF");
    }
  };

  const load = () => {
    api.get("/invoice").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
    api.get("/invoice/klien").then(({ data }) => setKlienList(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (inv, status) => {
    try {
      await api.put(`/invoice/${inv.id}`, { Status_Pembayaran: status });
      toast.success(`${inv.No_Invoice} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openSend = (inv) => {
    const known = inv.Email_Klien || (invoices || []).find((x) => x.Klien_Dituju === inv.Klien_Dituju && x.Email_Klien)?.Email_Klien || "";
    setSendEmail(known);
    setSendTarget(inv);
  };

  const submitSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/invoice/${sendTarget.id}/kirim`, { email: sendEmail.trim() });
      toast.success(data.message);
      setSendTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Hapus ${inv.No_Invoice}? Trip terkait bisa ditagihkan kembali.`)) return;
    try {
      await api.delete(`/invoice/${inv.id}`);
      toast.success("Invoice dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="invoice-page" className="space-y-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <ReceiptText className="w-7 h-7 text-emerald-700" /> Penagihan Klien
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Invoice tagihan angkutan berdasarkan tonase bongkar trip yang sudah Selesai</p>
        </div>
        <Button data-testid="create-invoice-button" onClick={() => setDraftOpen(true)}
          className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 shrink-0">
          <Plus className="w-4 h-4 mr-2" /> Buat Draft Invoice
        </Button>
      </div>

      <RekapTagihanBulanan refreshKey={invoices} />

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        <div data-testid="invoice-filter-komoditas" className="flex flex-wrap items-center gap-2 px-5 pt-4 pb-1">
          <span className="text-xs uppercase tracking-wider text-zinc-500 font-medium mr-1">Komoditas:</span>
          {KOMODITAS_OPSI.map((k) => (
            <button key={k} type="button" data-testid={`filter-komoditas-${k}`}
              onClick={() => setFilterKomoditas(k)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-200 ${
                filterKomoditas === k
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-zinc-600 border-zinc-300 hover:border-emerald-600 hover:text-emerald-700"
              }`}>
              {k === "Semua" ? "Semua" : k}
              {k !== "Semua" && invoices && (
                <span className="ml-1 opacity-70">({invoices.filter((i) => i.Jenis_Komoditas === k).length})</span>
              )}
            </button>
          ))}
        </div>
        {invoices === null ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : invoices.length === 0 ? (
          <p data-testid="invoice-empty" className="px-5 py-14 text-center text-sm text-zinc-400">
            Belum ada invoice. Klik "Buat Draft Invoice" untuk menarik trip Selesai yang siap ditagihkan.
          </p>
        ) : tampil.length === 0 ? (
          <p data-testid="invoice-filter-empty" className="px-5 py-10 text-center text-sm text-zinc-400">
            Tidak ada invoice komoditas {filterKomoditas} — pilih filter lain.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoice-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["No Invoice", "Tgl Terbit", "Klien", "Komoditas", "Periode", "Trip", "Tonase Ditagihkan", "Harga/KG", "Total Tagihan", "Status Pembayaran", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tampil.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.No_Invoice}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                    <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{inv.No_Invoice}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(inv.Tanggal_Terbit)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{inv.Klien_Dituju}</td>
                    <td data-testid={`invoice-komoditas-${inv.No_Invoice}`} className="px-4 py-3 whitespace-nowrap">
                      <Badge variant="outline" className={inv.Jenis_Komoditas && inv.Jenis_Komoditas !== "Semua" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-zinc-50 text-zinc-600 border-zinc-200"}>
                        {inv.Jenis_Komoditas || "Semua"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-500 text-xs">{fmtDate(inv.Periode_Dari)} – {fmtDate(inv.Periode_Sampai)}</td>
                    <td className="px-4 py-3 tabular-nums text-center">{(inv.trips || []).length}</td>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtKG(inv.Total_Tonase_Ditagihkan)}</td>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtRp(inv.Harga_Angkut_Per_KG)}</td>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap font-semibold text-emerald-800">{fmtRp(inv.Total_Tagihan)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Select value={inv.Status_Pembayaran} onValueChange={(v) => changeStatus(inv, v)}>
                        <SelectTrigger data-testid={`invoice-status-${inv.No_Invoice}`} className="h-8 w-40 border-0 bg-transparent p-0 focus:ring-0">
                          <Badge className={`${statusBadge[inv.Status_Pembayaran]} font-medium cursor-pointer`}>{inv.Status_Pembayaran}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_PEMBAYARAN.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Button data-testid={`invoice-detail-${inv.No_Invoice}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => setDetail(inv)} title="Lihat rincian trip">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`invoice-pdf-${inv.No_Invoice}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => downloadPdf(inv)} title="Unduh PDF invoice">
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`invoice-oa-${inv.No_Invoice}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => setOaTarget(inv)} title="Unduh PDF format resmi (OA)">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`invoice-send-${inv.No_Invoice}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => openSend(inv)} title="Kirim invoice ke email klien">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`invoice-delete-${inv.No_Invoice}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-red-600"
                          onClick={() => remove(inv)} title="Hapus invoice">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DraftDialog open={draftOpen} onClose={() => setDraftOpen(false)} klienList={klienList} onSaved={load} />
      <OADialog target={oaTarget} onClose={() => setOaTarget(null)} onDone={async (inv) => { await downloadPdf(inv, "oa"); load(); }} />

      <Dialog open={!!sendTarget} onOpenChange={(v) => !v && setSendTarget(null)}>
        <DialogContent data-testid="invoice-send-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kirim {sendTarget?.No_Invoice} ke Email Klien</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitSend} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Alamat Email Klien ({sendTarget?.Klien_Dituju})</Label>
              <Input data-testid="invoice-send-email-input" type="email" required placeholder="finance@klien.co.id"
                value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} />
              {sendTarget?.Terkirim_Pada && (
                <p className="text-xs text-zinc-400">Terakhir terkirim ke {sendTarget.Terkirim_Ke}</p>
              )}
            </div>
            <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
              Email berisi ringkasan tagihan dengan lampiran PDF invoice resmi berkop perusahaan.
            </p>
            <DialogFooter>
              <Button data-testid="invoice-send-cancel" type="button" variant="outline" onClick={() => setSendTarget(null)}>Batal</Button>
              <Button data-testid="invoice-send-submit" type="submit" disabled={sending}
                className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Kirim Invoice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="invoice-detail-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.No_Invoice} — {detail?.Klien_Dituju}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                  <p className="text-xs text-zinc-500">Tonase Ditagihkan</p>
                  <p className="font-semibold tabular-nums">{fmtKG(detail.Total_Tonase_Ditagihkan)}</p>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                  <p className="text-xs text-zinc-500">Harga Angkut / KG</p>
                  <p className="font-semibold tabular-nums">{fmtRp(detail.Harga_Angkut_Per_KG)}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <p className="text-xs text-emerald-700">Total Tagihan</p>
                  <p className="font-semibold tabular-nums text-emerald-800">{fmtRp(detail.Total_Tagihan)}</p>
                </div>
              </div>
              <table className="w-full text-sm" data-testid="invoice-detail-trips-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["ID Trip", "Tgl Muat", "Plat", "Supir", "Komoditas", "Bongkar (KG)"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(detail.trips || []).map((t) => (
                    <tr key={t.ID_Trip} className="border-b border-zinc-100">
                      <td className="px-3 py-2 font-medium">{t.ID_Trip}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(t.Tanggal_Muat)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{t.Plat_Truk}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{t.Nama_Supir}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{t.Jenis_Komoditas}</td>
                      <td className="px-3 py-2 tabular-nums">{Number(t.Tonase_Bongkar_KG).toLocaleString("id-ID")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
